import React, { useState, useEffect, useMemo } from 'react'
import { subscribe } from 'react-contextual'
import { format } from 'date-fns'
import { map } from 'lodash-es'
import { AggregateTable, Header } from '../../..'
import { PortfoliosProvider, SearchFilterProvider } from '../../../../providers'
import { stringUtils } from '../../../../../utils'
import PropertyIcon from '../../../../components/atoms/Svgs/Property'
import styles from './PortfolioTable.module.scss'

/** A portfolio as consumed by this table (subset of fields off the provider). */
interface Portfolio {
  id: string
  streetAddress: string
  createdAt: number | string | Date
  updatedAt: number | string | Date
}

/** A portfolio row after formatting, as fed to the AggregateTable. */
interface PortfolioRow {
  id: string
  streetAddress: string
  created: string
  updated: string
}

interface PortfolioTableProps {
  // PortfoliosProvider
  portfolios?: Portfolio[]
  openPortfolio: (id: string) => void
  // SearchFilterProvider
  searchQuery: string
}

const PortfolioTable = ({ portfolios = [], openPortfolio, searchQuery }: PortfolioTableProps): React.ReactElement => {
  const [data, setData] = useState<Portfolio[]>([])
  useEffect(() => {
    setData(portfolios)
  }, [portfolios])

  const filteredPortfolios = useMemo(() => {
    return data.filter(inv => stringUtils.jsonStringSearch(searchQuery, inv))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery])

  const columns = [
    {
      Header: 'Property',
      accessor: 'streetAddress',
    },
    {
      Header: 'Created',
      accessor: 'created',
    },
    {
      Header: 'Updated',
      accessor: 'updated',
    },
  ]

  const portfolioData = useMemo(() => {
    const buildPortfolioObj = (p: Portfolio): PortfolioRow => ({
      id: p.id,
      streetAddress: p.streetAddress,
      created: format(new Date(p.createdAt), 'dd/MM/yyyy'),
      updated: format(new Date(p.updatedAt), 'dd/MM/yyyy'),
    })

    return searchQuery !== '' ? map(filteredPortfolios, buildPortfolioObj) : map(data, buildPortfolioObj)
  }, [data, filteredPortfolios, searchQuery])

  return useMemo(
    () => (
      <div className={styles.root}>
        <Header icon={<PropertyIcon />} text="Properties" />
        <AggregateTable
          columns={columns}
          data={portfolioData}
          onRowClick={(row: PortfolioRow) => openPortfolio(row.id)}
        />
      </div>
    ),
    [columns, openPortfolio, portfolioData],
  )
}

export default subscribe(
  [PortfoliosProvider, SearchFilterProvider],
  (
    { portfolios, openPortfolio }: Pick<PortfolioTableProps, 'portfolios' | 'openPortfolio'>,
    { searchQuery }: Pick<PortfolioTableProps, 'searchQuery'>,
  ) => ({
    portfolios,
    openPortfolio,
    searchQuery,
  }),
)(PortfolioTable)
